/**
 * Auto-checkin rules of the spot list, ported from the native app (TaskData.isAutoCheckinTask,
 * TaskListManager.getTasksThatCanBeCalledByAutoCheckin). Pure functions over the spot objects of
 * the list response; the event flags come in as options so the rules run without the store.
 */

// values as in src/constants/config.js (CHECKIN_*, SPOT_STATE_*, SPOT_IMG_TYPE_SECRET)
const CHECKIN_GPS = 0;
const CHECKIN_FREE = 1;
const CHECKIN_IBEACON = 3;
const SPOT_STATE_ACTIVE = 1;
const SPOT_STATE_SOLVED = 3;
const SPOT_STATE_FAILED = 4;
const SPOT_TYPE_SECRET = '20';

/** GPS proximity in meters that stands in for beacon ranging, which no client has. */
export const IBEACON_AUTO_CHECKIN_RANGE_M = 3;

/**
 * Whether the spot is an auto-checkin spot: an info spot that is not played yet and, by checkin
 * type, GPS: secret or (auto_checkin on and GPS not disabled); free: secret; iBeacon: secret or
 * auto_checkin on; QR, AR, password: never. Distance, position and the inactive state play no
 * role, so the classification stays stable while the player moves.
 * @param {object} spot entry of the spot list
 * @param {{autoCheckinOn: boolean, gpsDisabled: boolean}} flags event settings auto_checkin and disable_gps
 */
export function isAutoCheckinSpot(spot, flags) {
    if (!spot || spot.raetsel || isPlayed(spot)) {
        return false;
    }
    const secret = spot.typ === SPOT_TYPE_SECRET;
    const autoCheckinOn = !!(flags && flags.autoCheckinOn);
    const gpsDisabled = !!(flags && flags.gpsDisabled);
    switch (spot.indoor) {
        case CHECKIN_GPS:
            return secret || (autoCheckinOn && !gpsDisabled);
        case CHECKIN_FREE:
            return secret;
        case CHECKIN_IBEACON:
            return secret || autoCheckinOn;
        default:
            return false;
    }
}

/**
 * Whether the auto-checkin of the spot may fire now: an active auto-checkin spot that is in range.
 * GPS: within scr, or at once when GPS is disabled in the event; free: at once; iBeacon: within
 * the fixed 3 m. Without a position (distance null) GPS and iBeacon spots never fire.
 */
export function canAutoCheckinNow(spot, flags) {
    if (!isAutoCheckinSpot(spot, flags) || spot.tstatus !== SPOT_STATE_ACTIVE) {
        return false;
    }
    switch (spot.indoor) {
        case CHECKIN_GPS:
            return !!(flags && flags.gpsDisabled) || isWithin(spot, spot.scr);
        case CHECKIN_FREE:
            return true;
        default:
            return isWithin(spot, IBEACON_AUTO_CHECKIN_RANGE_M);
    }
}

function isPlayed(spot) {
    return spot.tstatus === SPOT_STATE_SOLVED || spot.tstatus === SPOT_STATE_FAILED;
}

/** distanceToCurrentPosition is in km, null without a position; a missing range never matches. */
function isWithin(spot, rangeM) {
    const distanceKm = spot.distanceToCurrentPosition;
    const range = Number(rangeM);
    return typeof distanceKm === 'number' && isFinite(distanceKm) && isFinite(range) && distanceKm * 1000 <= range;
}
